/**
 * /admin/customers — list of every portal-active customer with last-login
 * info and a "View as customer" button per row.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, Bug, CreditCard, Loader2, LogOut, MapPin, RefreshCw, Search, Eye, CircleDot, Truck, X, Zap,
  CheckCircle2, AlertCircle, Megaphone, Layers,
} from 'lucide-react';

import { adminApi } from '@/lib/api';
import AdminLinkLocationModal from '@/components/AdminLinkLocationModal';
import AdminServiceInfoModal from '@/components/AdminServiceInfoModal';
import AdminDiagnoseLoginModal from '@/components/AdminDiagnoseLoginModal';
import AdminAnnouncementModal from '@/components/AdminAnnouncementModal';
import AdminBulkServiceInfoModal from '@/components/AdminBulkServiceInfoModal';

function fmtWhen(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = Math.round((now - d) / 60000); // minutes
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff}m ago`;
    if (diff < 60 * 24) return `${Math.round(diff / 60)}h ago`;
    if (diff < 60 * 24 * 7) return `${Math.round(diff / (60 * 24))}d ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

function StatusPill({ status }) {
  const map = {
    active:            { color: 'bg-emerald-100 text-emerald-800 border-emerald-200', label: 'Active' },
    never_signed_in:   { color: 'bg-amber-50 text-amber-800 border-amber-200',       label: 'Never signed in' },
    blocked:           { color: 'bg-red-100 text-red-800 border-red-200',            label: 'Blocked' },
    removed_from_hub:  { color: 'bg-red-100 text-red-800 border-red-200',            label: 'Removed from Hub' },
    hidden_by_admin:   { color: 'bg-gray-200 text-gray-800 border-gray-300',         label: 'Removed by you' },
    hub_unreachable:   { color: 'bg-gray-100 text-gray-700 border-gray-200',         label: 'Hub unreachable' },
  };
  const cfg = map[status] || { color: 'bg-gray-100 text-gray-700 border-gray-200', label: status };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border ${cfg.color} px-2 py-0.5 text-[11px] font-semibold`}>
      <CircleDot className="h-2.5 w-2.5" />
      {cfg.label}
    </span>
  );
}

export default function AdminCustomers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState(null); // null=loading, [] = loaded
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [q, setQ] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [chargeTarget, setChargeTarget] = useState(null); // customer row for confirm modal
  const [chargeBusy, setChargeBusy] = useState(false);
  const [chargeResult, setChargeResult] = useState(null); // { charged, failed, results, total }
  const [linkTarget, setLinkTarget] = useState(null); // customer row for link modal
  const [svcTarget, setSvcTarget] = useState(null);   // customer row for service info modal
  const [diagInitial, setDiagInitial] = useState(null); // opens diagnose modal, string or ''
  const [includeRemoved, setIncludeRemoved] = useState(false);
  const [syncSummary, setSyncSummary] = useState(null);
  const [showSyncFlash, setShowSyncFlash] = useState(false);
  const [announceOpen, setAnnounceOpen] = useState(false);
  const [bulkSvcOpen, setBulkSvcOpen] = useState(false);
  const syncFlashTimerRef = useRef(null);

  const load = useCallback(async ({ userInitiated = false } = {}) => {
    try {
      setRefreshing(true);
      const { data } = await adminApi.customers({ includeRemoved });
      setCustomers(data?.customers || []);
      setSyncSummary(data?.sync_summary || null);
      setError('');
      if (userInitiated) {
        setShowSyncFlash(true);
        if (syncFlashTimerRef.current) window.clearTimeout(syncFlashTimerRef.current);
        syncFlashTimerRef.current = window.setTimeout(() => setShowSyncFlash(false), 8000);
      }
    } catch (err) {
      if (err?.status === 401) {
        navigate('/admin', { replace: true });
        return;
      }
      setError(err?.detail || 'Failed to load customers.');
    } finally {
      setRefreshing(false);
    }
  }, [navigate, includeRemoved]);

  useEffect(() => () => {
    if (syncFlashTimerRef.current) window.clearTimeout(syncFlashTimerRef.current);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!customers) return null;
    const s = q.trim().toLowerCase();
    if (!s) return customers;
    return customers.filter((c) => {
      const hay = [
        c.business_name, c.service_address,
        ...(c.emails || []), ...(c.phones || []),
        c.zoho_customer_id,
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(s);
    });
  }, [customers, q]);

  const toggleHidden = async (c) => {
    if (busyId) return;
    const hiding = !c.hidden_by_admin;
    if (
      hiding &&
      !window.confirm(
        `Remove ${c.business_name} from the portal?\n\nThey will be hidden from this list and won't be able to sign in. Their data is kept, and you can restore them anytime via "Show hidden".`,
      )
    ) {
      return;
    }
    setBusyId(c.portal_customer_id);
    setError('');
    try {
      if (hiding) {
        await adminApi.removeCustomer(c.portal_customer_id);
      } else {
        await adminApi.restoreCustomer(c.portal_customer_id);
      }
      await load({ userInitiated: true });
    } catch (err) {
      setError(err?.detail || 'Action failed.');
    } finally {
      setBusyId(null);
    }
  };

  const impersonate = async (c) => {
    if (busyId) return;
    setBusyId(c.portal_customer_id);
    try {
      await adminApi.impersonateById(c.portal_customer_id, 'From admin console');
      navigate('/portal/dashboard');
    } catch (err) {
      // Show the server's own explanation when there is one; otherwise fall
      // back to a generic message with the HTTP status for support.
      let msg = err?.detail || 'Impersonation failed.';
      if (err?.status && !String(msg).includes(String(err.status))) {
        msg = `${msg} (HTTP ${err.status})`;
      }
      setError(msg);
      setBusyId(null);
    }
  };

  const logout = async () => {
    try { await adminApi.logout(); } catch { /* ignore */ }
    navigate('/admin', { replace: true });
  };

  const runCharge = async () => {
    if (!chargeTarget || chargeBusy) return;
    setChargeBusy(true);
    setChargeResult(null);
    try {
      const { data } = await adminApi.chargeAutopay(chargeTarget.account_id, {
        reason: 'Admin manual autopay run',
      });
      setChargeResult(data);
      // Reload the list so current_balance reflects the new payments.
      load();
    } catch (err) {
      setChargeResult({
        ok: false,
        charged: 0,
        failed: 0,
        total_charged_amount: 0,
        results: [],
        error: err?.detail || 'Charge failed. Check the backend logs.',
      });
    } finally {
      setChargeBusy(false);
    }
  };

  const closeChargeModal = () => {
    setChargeTarget(null);
    setChargeResult(null);
    setChargeBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#F5F1EA]">
      <header className="bg-[#0B1930] text-white px-6 py-4 flex items-center justify-between shadow">
        <div className="flex items-center gap-3">
          <Eye className="h-5 w-5 text-[#C8A46B]" />
          <div>
            <h1 className="font-heading uppercase tracking-wide text-lg">ZMEX Portal Admin</h1>
            <p className="text-[11px] text-white/70 -mt-0.5">
              {customers ? `${customers.length} customer${customers.length === 1 ? '' : 's'}` : '…'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setDiagInitial('')}
            className="inline-flex items-center gap-1 rounded-full bg-white/10 hover:bg-white/20 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide transition-colors"
            data-testid="admin-diagnose-login-open"
          >
            <Bug className="h-3.5 w-3.5" />
            Diagnose Login
          </button>
          <button
            type="button"
            onClick={() => load({ userInitiated: true })}
            disabled={refreshing}
            className="inline-flex items-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#0B1930] px-4 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors disabled:opacity-60 shadow"
            data-testid="admin-sync-from-hub"
            title="Pull the latest customer list + service info from the ZMEX Hub"
          >
            {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
            {refreshing ? 'Syncing…' : 'Sync from Hub'}
          </button>
          <button
            type="button"
            onClick={() => setBulkSvcOpen(true)}
            className="inline-flex items-center gap-1 rounded-full bg-white/10 hover:bg-white/20 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide transition-colors"
            data-testid="admin-bulk-service-open"
            title="Apply container / schedule to many accounts at once"
          >
            <Layers className="h-3.5 w-3.5" />
            Bulk Service Info
          </button>
          <button
            type="button"
            onClick={() => setAnnounceOpen(true)}
            className="inline-flex items-center gap-1 rounded-full bg-white/10 hover:bg-white/20 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide transition-colors"
            data-testid="admin-announcement-open"
            title="Edit the login reminder announcement customers see"
          >
            <Megaphone className="h-3.5 w-3.5" />
            Announcement
          </button>
          <button
            type="button"
            onClick={logout}
            className="inline-flex items-center gap-1 rounded-full bg-white/10 hover:bg-white/20 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide transition-colors"
            data-testid="admin-customers-logout"
          >
            <LogOut className="h-3.5 w-3.5" />
            Sign out
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {showSyncFlash && syncSummary && (
          <div
            className="mb-4 rounded-2xl bg-emerald-50 border border-emerald-200 px-4 py-3 flex items-start gap-3"
            data-testid="admin-sync-flash"
          >
            <CheckCircle2 className="h-5 w-5 text-emerald-700 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-emerald-900 uppercase tracking-wide">
                Synced {syncSummary.total} customer{syncSummary.total === 1 ? '' : 's'} from the Hub
              </p>
              <p className="text-xs text-emerald-900 mt-0.5">
                <span className="font-semibold">{syncSummary.hub_ok}</span> got full service info
                {syncSummary.phone_only > 0 && (
                  <> · <span className="font-semibold text-amber-800">{syncSummary.phone_only}</span> phone-only (need manual entry)</>
                )}
                {syncSummary.hub_error > 0 && (
                  <> · <span className="font-semibold text-red-800">{syncSummary.hub_error}</span> Hub error</>
                )}
                {syncSummary.with_manual_override > 0 && (
                  <> · <span className="font-semibold">{syncSummary.with_manual_override}</span> manual override{syncSummary.with_manual_override === 1 ? '' : 's'} preserved</>
                )}
                {syncSummary.hub_allowlist_ok === false && (
                  <> · <span className="font-semibold text-red-800">Hub allowlist unreachable — showing cached list</span></>
                )}
              </p>
              {(syncSummary.contact_changes || []).length > 0 && (
                <div
                  className="mt-2 rounded-xl bg-white border border-emerald-300 px-3 py-2"
                  data-testid="admin-sync-flash-contact-changes"
                >
                  <p className="text-[11px] uppercase tracking-wide font-bold text-emerald-900 mb-1">
                    Contact info updated for {syncSummary.contact_changes.length} customer{syncSummary.contact_changes.length === 1 ? '' : 's'}
                  </p>
                  <ul className="space-y-1">
                    {syncSummary.contact_changes.map((ch) => (
                      <li
                        key={ch.portal_customer_id}
                        className="text-[11px] text-[#0B1930]"
                      >
                        <span className="font-semibold">{ch.business_name}</span>
                        {ch.changes?.phones && (
                          <span>
                            {' — phones: '}
                            {(ch.changes.phones.removed || []).map((p) => (
                              <span key={`r-${p}`} className="line-through text-red-700 mr-1">{p}</span>
                            ))}
                            {(ch.changes.phones.added || []).map((p) => (
                              <span key={`a-${p}`} className="font-semibold text-emerald-800 mr-1">+{p}</span>
                            ))}
                          </span>
                        )}
                        {ch.changes?.emails && (
                          <span>
                            {' — emails: '}
                            {(ch.changes.emails.removed || []).map((e) => (
                              <span key={`re-${e}`} className="line-through text-red-700 mr-1">{e}</span>
                            ))}
                            {(ch.changes.emails.added || []).map((e) => (
                              <span key={`ae-${e}`} className="font-semibold text-emerald-800 mr-1">+{e}</span>
                            ))}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => setShowSyncFlash(false)}
              className="text-emerald-800 hover:text-emerald-900 shrink-0"
              data-testid="admin-sync-flash-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        <div className="mb-6 flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by business, email, phone, or address..."
              className="w-full rounded-full bg-white border border-[#EDE5D4] pl-9 pr-4 py-2.5 text-sm text-[#0B1930] focus:outline-none focus:border-[#C8A46B]"
              data-testid="admin-customers-search"
            />
          </div>
          <label
            className="inline-flex items-center gap-2 rounded-full bg-white border border-[#EDE5D4] px-3 py-2 text-xs font-semibold text-[#0B1930] cursor-pointer hover:bg-[#FAF7F0]"
            title="Include customers whose Hub Portal Access toggle is OFF"
          >
            <input
              type="checkbox"
              checked={includeRemoved}
              onChange={(e) => setIncludeRemoved(e.target.checked)}
              className="rounded"
              data-testid="admin-customers-include-removed"
            />
            Show hidden
          </label>
        </div>

        {error && (
          <div className="mb-4 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700" data-testid="admin-customers-error">
            {error}
          </div>
        )}

        {customers === null && (
          <div className="flex items-center justify-center py-20 text-[#4B5563] gap-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading customers…</span>
          </div>
        )}

        {customers !== null && (filtered || []).length === 0 && (
          <div className="rounded-2xl bg-white border border-[#EDE5D4] p-8 text-center text-sm text-[#4B5563]">
            {q ? 'No customers match that search.' : 'No customers have signed in or been impersonated yet.'}
          </div>
        )}

        {customers !== null && (filtered || []).length > 0 && (
          <div className="rounded-2xl bg-white border border-[#EDE5D4] overflow-hidden shadow-sm">
            <div className="divide-y divide-[#EDE5D4]">
              {filtered.map((c) => (
                <div
                  key={c.portal_customer_id}
                  className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-3 items-center px-5 py-4 hover:bg-[#FAF7F0] transition-colors"
                  data-testid={`admin-customer-row-${c.portal_customer_id}`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <p className="text-sm font-bold uppercase tracking-wide text-[#0B1930] truncate">
                        {c.business_name}
                      </p>
                      <StatusPill status={c.status} />
                    </div>
                    {c.service_address && (
                      <p className="text-xs text-[#4B5563] mt-0.5 truncate">
                        {c.service_address}
                      </p>
                    )}
                    <p className="text-xs text-[#4B5563] mt-1 truncate">
                      {(c.emails || [])[0] || (c.phones || [])[0] || '—'}
                      {c.current_balance > 0 && (
                        <span className="ml-3 font-semibold text-[#B8925A]">
                          ${c.current_balance.toFixed(2)} due
                        </span>
                      )}
                      {c.autopay_enrolled && (
                        <span
                          className="ml-3 inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-800"
                          data-testid={`admin-autopay-pill-${c.portal_customer_id}`}
                        >
                          <CreditCard className="h-2.5 w-2.5" />
                          Autopay
                        </span>
                      )}
                    </p>
                    {c.hub_profile_status === 'ok' && (c.container_size || c.collection_frequency) && (
                      <p
                        className="text-[11px] text-[#4B5563] mt-1 truncate"
                        data-testid={`admin-service-summary-${c.portal_customer_id}`}
                      >
                        <Truck className="inline h-3 w-3 mr-1 -mt-0.5 text-emerald-700" />
                        <span className="font-semibold text-[#0B1930]">
                          {c.container_size || '—'}
                        </span>
                        {c.collection_frequency && (
                          <span className="text-[#4B5563]"> · {c.collection_frequency}</span>
                        )}
                        {c.has_manual_service_override && (
                          <span className="ml-2 text-[9px] uppercase tracking-wide font-bold text-amber-800">
                            manual override
                          </span>
                        )}
                      </p>
                    )}
                    {c.hub_profile_status === 'phone_only_no_email' && !c.has_manual_service_override && (
                      <button
                        type="button"
                        onClick={() => setSvcTarget(c)}
                        className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 hover:bg-amber-100 text-amber-900 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide transition-colors"
                        data-testid={`admin-service-warning-${c.portal_customer_id}`}
                        title="Hub cannot deliver service info for phone-only customers. Click to enter it manually or add an email to their Hub record."
                      >
                        <AlertCircle className="h-3 w-3" />
                        Service info missing (phone-only)
                      </button>
                    )}
                    {c.hub_profile_status === 'hub_error' && (
                      <p
                        className="text-[10px] text-red-700 mt-1"
                        data-testid={`admin-service-error-${c.portal_customer_id}`}
                      >
                        <AlertCircle className="inline h-3 w-3 mr-1 -mt-0.5" />
                        Hub couldn&apos;t return service info · check backend logs
                      </p>
                    )}
                  </div>

                  <div className="text-right sm:min-w-[130px]">
                    <p className="text-[11px] uppercase tracking-wide text-[#4B5563]">Last login</p>
                    <p className="text-sm font-semibold text-[#0B1930]">
                      {fmtWhen(c.last_login_at) || <span className="text-[#9CA3AF]">Never</span>}
                    </p>
                    {c.last_login_via && (
                      <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF]">
                        via {c.last_login_via === 'sms' ? 'SMS' : 'Email'}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setLinkTarget(c)}
                      disabled={!!busyId || chargeBusy}
                      className="inline-flex items-center justify-center gap-1 rounded-full bg-white border border-[#EDE5D4] hover:bg-[#FAF7F0] text-[#0B1930] px-3 py-2 text-[11px] font-bold uppercase tracking-wide transition-colors disabled:opacity-60"
                      data-testid={`admin-link-location-${c.portal_customer_id}`}
                      title="Link another Zoho customer / location to this portal user"
                    >
                      <MapPin className="h-3 w-3" />
                      Locations
                    </button>
                    {c.account_id && (
                      <button
                        type="button"
                        onClick={() => setSvcTarget(c)}
                        disabled={!!busyId || chargeBusy}
                        className="inline-flex items-center justify-center gap-1 rounded-full bg-white border border-[#EDE5D4] hover:bg-[#FAF7F0] text-[#0B1930] px-3 py-2 text-[11px] font-bold uppercase tracking-wide transition-colors disabled:opacity-60"
                        data-testid={`admin-service-info-${c.portal_customer_id}`}
                        title="Edit container size / frequency / next pickup manually"
                      >
                        <Truck className="h-3 w-3" />
                        Service Info
                      </button>
                    )}
                    {c.autopay_enrolled && (
                      <button
                        type="button"
                        onClick={() => { setChargeResult(null); setChargeTarget(c); }}
                        disabled={!!busyId || chargeBusy}
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 text-xs font-bold uppercase tracking-wide transition-colors disabled:opacity-60"
                        data-testid={`admin-charge-autopay-${c.portal_customer_id}`}
                        title="Charge every open invoice against the saved default card"
                      >
                        <CreditCard className="h-3.5 w-3.5" />
                        Charge Autopay
                      </button>
                    )}
                    {c.hidden_by_admin ? (
                      <button
                        type="button"
                        onClick={() => toggleHidden(c)}
                        disabled={!!busyId}
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 text-xs font-bold uppercase tracking-wide transition-colors disabled:opacity-60"
                        data-testid={`admin-restore-${c.portal_customer_id}`}
                        title="Bring this customer back to the portal"
                      >
                        {busyId === c.portal_customer_id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <CheckCircle2 className="h-3.5 w-3.5" />
                        )}
                        Restore
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => impersonate(c)}
                          disabled={!!busyId}
                          className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0B1930] hover:bg-[#111A2E] text-white px-4 py-2 text-xs font-bold uppercase tracking-wide transition-colors disabled:opacity-60"
                          data-testid={`admin-view-as-${c.portal_customer_id}`}
                        >
                          {busyId === c.portal_customer_id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <ArrowRight className="h-3.5 w-3.5" />
                          )}
                          View as
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleHidden(c)}
                          disabled={!!busyId}
                          className="inline-flex items-center justify-center gap-1 rounded-full bg-white border border-red-200 hover:bg-red-50 text-red-700 px-3 py-2 text-[11px] font-bold uppercase tracking-wide transition-colors disabled:opacity-60"
                          data-testid={`admin-remove-${c.portal_customer_id}`}
                          title="Hide this customer from the portal (deleted customers). Their data is kept; restore anytime via Show hidden."
                        >
                          <X className="h-3.5 w-3.5" />
                          Remove
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="mt-6 text-[11px] text-[#9CA3AF] text-center">
          Only customers who have ever attempted a sign-in or been impersonated appear here.
          Customers on the Hub allowlist who have never touched the portal will appear the moment they log in for the first time.
        </p>
      </main>

      {chargeTarget && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          data-testid="admin-charge-modal"
          onClick={(e) => { if (e.target === e.currentTarget && !chargeBusy) closeChargeModal(); }}
        >
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#EDE5D4]">
              <div className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-emerald-600" />
                <h2 className="font-heading uppercase tracking-wide text-base text-[#0B1930]">
                  Charge Autopay
                </h2>
              </div>
              <button
                type="button"
                onClick={closeChargeModal}
                disabled={chargeBusy}
                className="text-[#4B5563] hover:text-[#0B1930] disabled:opacity-40"
                data-testid="admin-charge-modal-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-5 py-5">
              <p className="text-sm text-[#0B1930] font-semibold">
                {chargeTarget.business_name}
              </p>
              <p className="text-xs text-[#4B5563] mt-0.5">
                {chargeTarget.service_address || '—'}
              </p>

              {!chargeResult && !chargeBusy && (
                <div className="mt-4 rounded-xl bg-[#FAF7F0] border border-[#EDE5D4] px-4 py-3">
                  <p className="text-sm text-[#0B1930]">
                    Charge every open Zoho invoice against the customer&apos;s saved default card. This runs
                    <span className="font-semibold"> off-session</span> — no customer approval is required.
                  </p>
                  {chargeTarget.current_balance > 0 && (
                    <p className="text-xs text-[#4B5563] mt-2">
                      Cached balance:
                      <span className="font-semibold text-[#0B1930]"> ${chargeTarget.current_balance.toFixed(2)}</span>
                      <span className="ml-1">(the actual charge is calculated live from Zoho at click time)</span>
                    </p>
                  )}
                  <p className="text-[11px] text-[#4B5563] mt-2">
                    Successful charges will be recorded in Zoho Books automatically.
                  </p>
                </div>
              )}

              {chargeBusy && (
                <div className="mt-4 flex items-center gap-2 text-sm text-[#0B1930]">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Charging card and syncing to Zoho…
                </div>
              )}

              {chargeResult && (
                <div className="mt-4 space-y-3" data-testid="admin-charge-result">
                  {chargeResult.error && (
                    <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800 flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                      <span>{chargeResult.error}</span>
                    </div>
                  )}
                  {!chargeResult.error && (
                    <div className={`rounded-xl border px-4 py-3 text-sm flex items-start gap-2 ${
                      chargeResult.failed === 0
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                        : 'bg-amber-50 border-amber-200 text-amber-900'
                    }`}>
                      {chargeResult.failed === 0 ? (
                        <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                      ) : (
                        <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                      )}
                      <div>
                        <p className="font-semibold">
                          {chargeResult.charged} charged (${(chargeResult.total_charged_amount || 0).toFixed(2)})
                          {chargeResult.failed > 0 && ` — ${chargeResult.failed} failed`}
                        </p>
                        {chargeResult.charged === 0 && chargeResult.failed === 0 && (
                          <p className="text-xs mt-0.5">No open invoices found for this customer.</p>
                        )}
                      </div>
                    </div>
                  )}

                  {(chargeResult.results || []).length > 0 && (
                    <div className="rounded-xl bg-white border border-[#EDE5D4] divide-y divide-[#EDE5D4]">
                      {chargeResult.results.map((r) => (
                        <div
                          key={r.invoice_id}
                          className="px-4 py-2.5 flex items-start justify-between gap-3"
                          data-testid={`admin-charge-result-row-${r.invoice_id}`}
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-[#0B1930]">
                              #{r.invoice_number}
                            </p>
                            {r.error && (
                              <p className="text-[11px] text-red-700 mt-0.5">{r.error}</p>
                            )}
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-sm font-bold text-[#0B1930]">
                              ${(r.amount || 0).toFixed(2)}
                            </p>
                            <p className={`text-[10px] uppercase tracking-wide font-bold ${
                              r.status === 'succeeded' ? 'text-emerald-700' : 'text-red-700'
                            }`}>
                              {r.status}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[#EDE5D4] bg-[#FAF7F0]">
              {!chargeResult ? (
                <>
                  <button
                    type="button"
                    onClick={closeChargeModal}
                    disabled={chargeBusy}
                    className="rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wide text-[#0B1930] hover:bg-[#EDE5D4] transition-colors disabled:opacity-60"
                    data-testid="admin-charge-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={runCharge}
                    disabled={chargeBusy}
                    className="inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 text-xs font-bold uppercase tracking-wide transition-colors disabled:opacity-60"
                    data-testid="admin-charge-confirm"
                  >
                    {chargeBusy ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <CreditCard className="h-3.5 w-3.5" />
                    )}
                    Charge now
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={closeChargeModal}
                  className="rounded-full bg-[#0B1930] hover:bg-[#111A2E] text-white px-4 py-2 text-xs font-bold uppercase tracking-wide transition-colors"
                  data-testid="admin-charge-close"
                >
                  Done
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {linkTarget && (
        <AdminLinkLocationModal
          customer={linkTarget}
          onClose={() => setLinkTarget(null)}
          onLinked={load}
        />
      )}
      {svcTarget && (
        <AdminServiceInfoModal
          customer={svcTarget}
          onClose={() => setSvcTarget(null)}
          onSaved={load}
        />
      )}
      {diagInitial !== null && (
        <AdminDiagnoseLoginModal
          initialContact={diagInitial}
          onClose={() => setDiagInitial(null)}
        />
      )}
      {announceOpen && (
        <AdminAnnouncementModal onClose={() => setAnnounceOpen(false)} />
      )}
      {bulkSvcOpen && (
        <AdminBulkServiceInfoModal
          customers={customers || []}
          onClose={() => setBulkSvcOpen(false)}
          onApplied={() => load({ userInitiated: true })}
        />
      )}
    </div>
  );
}
